import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    input,
    OnDestroy,
    OnInit,
    QueryList,
    signal,
    ViewChildren,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { StateService, PathNode } from '@uirouter/core';
import { Select, SelectFilterEvent } from 'primeng/select';
import { FocusTrap } from 'primeng/focustrap';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { SessionService } from '@monsido/core/session/session.service';
import { TotalDomainsService } from '@client/app/services/total-domains/total-domains.service';
import { CoreDomainService } from '@client/app/core/services/domain/core-domain.service';
import {
    DomainsService,
    DomainPickerValueType,
    DomainValue,
    DomainGroupValue,
} from '@client/app/services/domains/domains.service';
import { TranslocoModule } from '@jsverse/transloco';
import {
    buildDomainSwitchParams,
    DOMAIN_SWITCH_OPTIONS,
} from '@client/app/feature-modules/navigation/utils/domain-navigation.utils';
import { PATH_WHITELIST } from '@client/app/core/constants/helpers/path-whitelist';
import {
    DOMAIN_SWITCH_STATE_MAP,
    DOMAIN_SWITCH_STATE_PREFIXES,
    DOMAIN_SWITCH_STRIP_PARAMS,
    DomainSwitchStateMapEntry,
} from '@client/app/core/constants/helpers/domain-switch-state-map';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import { Domain } from '@monsido/modules/models/api/domain';

interface StateServiceWithPath {
    getCurrentPath(): Array<PathNode>;
    go(to: string, params?: Record<string, unknown>, options?: Record<string, unknown>): Promise<unknown>;
    readonly params: Record<string, unknown>;
}

type DomainPickerSearchValue = DomainValue & { searchKeywords?: string };

@Component({
    selector: 'mon-domain-picker',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        TranslocoModule,
        Select,
        FocusTrap,
    ],
    templateUrl: './domain-picker.component.html',
    styleUrl: './domain-picker.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DomainPickerComponent implements OnInit, OnDestroy {
    @ViewChildren(Select) private readonly selectInstances!: QueryList<Select>;
    private readonly totalDomainsService = inject(TotalDomainsService);
    private readonly coreDomainService = inject(CoreDomainService);
    private readonly sessionService = inject(SessionService);
    private readonly domainsService = inject(DomainsService);
    private readonly stateService = inject(StateService);
    private readonly activeFeatureService = inject(ActiveFeatureService);
    private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly destroyRef = inject(DestroyRef);

    readonly MAX_VISIBLE_DOMAINS = 200;

    readonly isCollapsed = input<boolean>(false);

    readonly allDomains = toSignal(this.totalDomainsService.domainsEssential, { initialValue: null });
    readonly totalDomainsCount = toSignal(this.totalDomainsService.totalDomains, { initialValue: null });

    readonly currentDomain = toSignal(this.sessionService.domainObs, { initialValue: this.sessionService.domain });
    readonly currentDomainGroup = toSignal(this.sessionService.domainGroupObs, { initialValue: this.sessionService.domainGroup });

    readonly selectedDomainItem = signal<DomainValue | DomainGroupValue | null>(null);

    readonly DomainPickerValueType = DomainPickerValueType;

    readonly filterValue = signal<string>('');
    readonly filterInput$ = new Subject<string>();

    readonly selectedDomainId = computed((): number | null => this.currentDomain()?.id ?? null);

    readonly flattenedDomains = computed((): (DomainValue | DomainGroupValue)[] => {
        const domains = this.allDomains();
        if (!domains) {
            return [];
        }
        const flattened = this.domainsService.flattenDomains(domains, true);
        return [
            ...flattened.filter(
                (d): d is DomainValue =>
                    d.type === DomainPickerValueType.Domain && (d).favorite === true,
            ),
            ...flattened.filter(
                (d) =>
                    !(d.type === DomainPickerValueType.Domain && (d).favorite === true),
            ),
        ];
    });

    readonly showInitialListing = computed((): boolean => {
        const count = this.totalDomainsCount();
        return count === null || count <= this.MAX_VISIBLE_DOMAINS;
    });

    readonly favouriteDomains = computed((): (DomainValue | DomainGroupValue)[] => {
        return this.flattenedDomains().filter(
            (d): boolean => d.type === DomainPickerValueType.Domain && (d as DomainValue).favorite === true,
        );
    });

    readonly visibleDomains = computed((): (DomainPickerSearchValue | DomainGroupValue)[] => {
        const filter = this.filterValue().trim().toLowerCase();
        const fullList = this.flattenedDomains();

        if (filter) {
            const filtered: (DomainPickerSearchValue | DomainGroupValue)[] = [];
            const added = new Set<string>();
            const matchedGroupTitlesByParent = new Map<number, Set<string>>();

            const pushUnique = (item: DomainPickerSearchValue | DomainGroupValue): void => {
                const key = `${item.type}:${item.id}`;
                if (added.has(key)) {
                    return;
                }
                added.add(key);
                filtered.push(item);
            };

            const getParentDomain = (group: DomainGroupValue): DomainValue | undefined =>
                fullList.find(
                    (item): item is DomainValue =>
                        item.type === DomainPickerValueType.Domain && item.id === group.domainId,
                );

            const matchesFilter = (item: DomainValue | DomainGroupValue): boolean => {
                const title = item.title ?? '';
                const url = item.type === DomainPickerValueType.Domain ? item.url ?? '' : '';
                return title.toLowerCase().includes(filter) || url.toLowerCase().includes(filter);
            };

            for (const item of fullList) {
                if (item.type !== DomainPickerValueType.DomainGroup || !matchesFilter(item)) {
                    continue;
                }
                const titles = matchedGroupTitlesByParent.get(item.domainId) ?? new Set<string>();
                if (item.title) {
                    titles.add(item.title);
                }
                matchedGroupTitlesByParent.set(item.domainId, titles);
            }

            for (const item of fullList) {
                if (!matchesFilter(item)) {
                    continue;
                }

                if (item.type === DomainPickerValueType.DomainGroup) {
                    const parent = getParentDomain(item);
                    if (parent) {
                        const searchKeywords = Array.from(
                            matchedGroupTitlesByParent.get(parent.id) ?? [],
                        ).join(' ');
                        pushUnique(searchKeywords ? { ...parent, searchKeywords } : parent);
                    }
                }

                pushUnique(item);
            }

            return filtered;
        }

        if (!this.showInitialListing()) {
            const favourites = this.favouriteDomains();
            const selected = this.selectedDomainItem();
            if (
                selected !== null &&
                !favourites.some((f): boolean => f.id === selected.id && f.type === selected.type)
            ) {
                return [selected, ...favourites];
            }
            return favourites;
        }

        return fullList;
    });

    readonly isDropdownOpen = signal<boolean>(false);
    readonly popupPosition = signal<{ top: number; left: number }>({ top: 0, left: 0 });
    readonly popupWidth = signal<number>(256);

    private readonly outsideClickHandler = (event: MouseEvent): void => {
        if (!this.elementRef.nativeElement.contains(event.target as Node)) {
            this.closeDropdown();
        }
    };

    constructor () {
        this.filterInput$.pipe(
            debounceTime(300),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe((value: string): void => {
            this.filterValue.set(value);
        });

        effect((): void => {
            const domain = this.currentDomain();
            const domainGroup = this.currentDomainGroup();
            if (domainGroup) {
                const match = this.flattenedDomains().find(
                    (d): boolean =>
                        d.type === DomainPickerValueType.DomainGroup && d.id === domainGroup.id,
                );
                this.selectedDomainItem.set(match ?? null);
            } else if (domain) {
                const match = this.flattenedDomains().find(
                    (d): boolean => d.type === DomainPickerValueType.Domain && d.id === domain.id,
                );
                this.selectedDomainItem.set(match ?? null);
            } else {
                this.selectedDomainItem.set(null);
            }
        });
    }

    ngOnInit (): void {
        document.addEventListener('mousedown', this.outsideClickHandler);
    }

    ngOnDestroy (): void {
        document.removeEventListener('mousedown', this.outsideClickHandler);
    }

    toggleDropdown (event: MouseEvent, triggerEl: HTMLElement): void {
        event.stopPropagation();

        const rect = triggerEl.getBoundingClientRect();
        const POPUP_MIN_WIDTH = 256;
        const POPUP_ESTIMATED_HEIGHT = 320;
        const MARGIN = 16;

        const resolvedWidth = Math.max(rect.width, POPUP_MIN_WIDTH);
        this.popupWidth.set(resolvedWidth);

        let top = rect.top;
        let left = rect.right + MARGIN;

        if (left + resolvedWidth > window.innerWidth) {
            left = rect.left - resolvedWidth - MARGIN;
        }
        if (left < MARGIN) {
            left = MARGIN;
        }

        if (top + POPUP_ESTIMATED_HEIGHT > window.innerHeight) {
            top = rect.top - POPUP_ESTIMATED_HEIGHT - MARGIN;
        }
        if (top < MARGIN) {
            top = MARGIN;
        }

        this.popupPosition.set({ top, left });
        this.isDropdownOpen.set(!this.isDropdownOpen());
        this.filterValue.set('');
        this.selectInstances?.forEach((s: Select): void => {
            s.resetFilter();
        });
    }

    closeDropdown (): void {
        this.isDropdownOpen.set(false);
        this.filterValue.set('');
        this.selectInstances?.forEach((s: Select): void => {
            s.resetFilter();
        });
    }

    onFilterChange (event: SelectFilterEvent): void {
        this.filterInput$.next(event.filter ?? '');
    }

    isFavouriteDomain (item: DomainValue | DomainGroupValue): boolean {
        return item.type === DomainPickerValueType.Domain && (item).favorite === true;
    }

    public hasDomainAccess (domain: DomainValue | DomainGroupValue): boolean {
        if (!this.sessionService.me || !domain?.id) {
            return false;
        }
        if (this.sessionService.hasRole('customer_admin')) {
            return true;
        }
        const agreement = this.sessionService.agreement;
        if (agreement && Array.isArray(agreement.domain_users)) {
            return agreement.domain_users.some(
                (du): boolean =>
                    du !== null &&
                    du.domain_id === domain.id &&
                    du.visible === true,
            );
        }
        return false;
    }

    selectDomain (item: DomainValue | DomainGroupValue): void {
        if (item.type === DomainPickerValueType.Domain && !this.hasDomainAccess(item)) {
            return;
        }
        const stateServiceWithPath = this.stateService as unknown as StateServiceWithPath;
        const path = stateServiceWithPath.getCurrentPath();
        const lastPathEntry = path[path.length - 1];
        const currentStateName = lastPathEntry?.state?.name ?? '';

        const queryParams: Record<string, unknown> = item.type === DomainPickerValueType.DomainGroup
            ? buildDomainSwitchParams(stateServiceWithPath.params, (item).domainId)
            : buildDomainSwitchParams(stateServiceWithPath.params, (item).id);

        if (item.type === DomainPickerValueType.DomainGroup) {
            queryParams['domain_group'] = (item).id;
        } else {
            delete queryParams['domain_group'];
            this.coreDomainService.removeDomainGroup();
        }

        const domainForFeatureCheck: Domain | undefined =
            item.type === DomainPickerValueType.Domain ? (item as DomainValue) : undefined;

        const mapEntry: DomainSwitchStateMapEntry | undefined = currentStateName
            ? DOMAIN_SWITCH_STATE_MAP[currentStateName]
            : undefined;

        const prefixFeature: string | undefined = currentStateName
            ? Object.keys(DOMAIN_SWITCH_STATE_PREFIXES).find(
                (prefix: string): boolean => currentStateName.startsWith(prefix),
            )
            : undefined;

        let targetState: string;

        if (mapEntry) {
            const featureActive = this.activeFeatureService.isFeatureActive(
                mapEntry.feature,
                domainForFeatureCheck,
            );
            targetState = featureActive ? mapEntry.to : 'base.customer.domain.dashboard';
            DOMAIN_SWITCH_STRIP_PARAMS.forEach((param: string): void => {
                delete queryParams[param];
            });
        } else if (prefixFeature) {
            targetState = this.activeFeatureService.isFeatureActive(
                DOMAIN_SWITCH_STATE_PREFIXES[prefixFeature],
                domainForFeatureCheck,
            )
                ? currentStateName
                : 'base.customer.domain.dashboard';
        } else {
            const isWhitelisted = path.some(
                (node: PathNode): boolean =>
                    (PATH_WHITELIST as readonly string[]).includes(node.state.name),
            );

            if (currentStateName === 'base.customer.domain.uptimes.show') {
                targetState = 'base.customer.domain.uptimes.index';
            } else if (isWhitelisted && currentStateName) {
                targetState = currentStateName;
            } else {
                targetState = 'base.customer.domain.dashboard';
            }
        }

        stateServiceWithPath
            .go(targetState, queryParams, DOMAIN_SWITCH_OPTIONS)
            .then((): void => {}, (): void => {})
            .finally((): void => {
                this.coreDomainService.changeDomain();
            });

        this.closeDropdown();
    }

    onPSelectChange (item: DomainValue | DomainGroupValue | null): void {
        if (item) {
            this.selectDomain(item);
        }
    }

    getDisplayTitle (): string {
        return this.currentDomain()?.title ?? this.currentDomainGroup()?.title ?? '';
    }

    getDomainTitleForGroup (item: DomainGroupValue): string {
        const domain = this.flattenedDomains().find(
            (d): d is DomainValue =>
                d.type === DomainPickerValueType.Domain && d.id === item.domainId,
        );
        return domain?.title ?? '';
    }
}
